/**
 * HTTP Client Service
 * Centralized HTTP client with authentication, error handling, and security features
 */

import { servicesConfig, buildUrl } from '../config/services.config';
import { authService } from './auth.service';

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
  success: boolean;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: any;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  retries?: number;
  requireAuth?: boolean;
  skipAuthRefresh?: boolean;
}

class HttpClient {
  private readonly defaultTimeout: number;
  private readonly defaultRetries: number;

  constructor() {
    this.defaultTimeout = servicesConfig.backend.timeout || 30000;
    this.defaultRetries = servicesConfig.backend.retries || 3;
  }

  /**
   * GET request
   */
  async get<T = any>(
    endpoint: string, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, undefined, options);
  }

  /**
   * POST request
   */
  async post<T = any>(
    endpoint: string, 
    data?: any, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data, options);
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string, 
    data?: any, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data, options);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    endpoint: string, 
    data?: any, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    endpoint: string, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  /**
   * Core request method
   */
  private async request<T>(
    method: string,
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      headers: customHeaders = {},
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
      requireAuth = true,
      skipAuthRefresh = false
    } = options;

    // Build full URL
    const url = this.buildFullUrl(endpoint);

    // Prepare headers
    const headers = await this.buildHeaders(customHeaders, requireAuth, skipAuthRefresh);

    // Prepare base request configuration (no signal — created fresh per attempt below)
    const requestConfig: RequestInit = { method, headers };

    // Add body for methods that support it
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      if (data instanceof FormData) {
        requestConfig.body = data;
        // Remove Content-Type header for FormData (browser sets it with boundary)
        delete (headers as any)['Content-Type'];
      } else {
        requestConfig.body = JSON.stringify(data);
      }
    }

    // Execute request with retries
    return this.executeWithRetries<T>(url, requestConfig, timeout, retries, data);
  }

  /**
   * Execute request with retry logic
   */
  private async executeWithRetries<T>(
    url: string,
    config: RequestInit,
    timeout: number,
    retries: number,
    requestPayload?: any
  ): Promise<ApiResponse<T>> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Fresh signal per attempt — a fired signal cannot be reused across retries
        const response = await fetch(url, { ...config, signal: AbortSignal.timeout(timeout) });
        return await this.handleResponse<T>(response, requestPayload);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on certain errors
        if (this.shouldNotRetry(error)) {
          break;
        }

        // Add delay between retries (exponential backoff)
        if (attempt < retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await this.sleep(delay);
        }
      }
    }

    throw this.createApiError(lastError || new Error('Request failed'));
  }

  /**
   * Handle fetch response
   */
  private async handleResponse<T>(response: Response, requestPayload?: any): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('Content-Type');
    const isJson = contentType?.includes('application/json');

    let responseData: any;

    try {
      if (isJson) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }
    } catch (error) {
      responseData = null;
    }

    if (!response.ok) {
      // For 422 errors, include detail field which typically contains validation errors
      const errorMessage = responseData?.detail || responseData?.message || `HTTP ${response.status}: ${response.statusText}`;

      if (import.meta.env.DEV) {
        console.error(`API Error ${response.status} ${response.statusText}: ${response.url}`);
      }

      throw this.createApiError(
        new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)),
        response.status,
        responseData?.code,
        responseData
      );
    }

    return {
      data: responseData,
      message: responseData?.message,
      status: response.status,
      success: true
    };
  }

  /**
   * Build full URL
   */
  private buildFullUrl(endpoint: string): string {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    return buildUrl('backend', endpoint);
  }

  /**
   * Build request headers
   */
  private async buildHeaders(
    customHeaders: Record<string, string>,
    requireAuth: boolean,
    skipAuthRefresh: boolean
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...customHeaders
    };

    // Add authentication headers
    if (requireAuth) {
      const accessToken = await this.getValidAccessToken(skipAuthRefresh);
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    // Add CSRF token if enabled
    if (servicesConfig.security.csrfEnabled) {
      const csrfToken = this.getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Add request ID for tracing
    headers['X-Request-ID'] = this.generateRequestId();

    return headers;
  }

  /**
   * Get valid access token (refresh if needed)
   */
  private async getValidAccessToken(skipRefresh: boolean = false): Promise<string | null> {
    const currentToken = authService.getAccessToken();

    if (!currentToken) {
      return null;
    }

    // Check if token needs refresh and attempt refresh
    if (!skipRefresh && authService.needsTokenRefresh()) {
      try {
        await authService.refreshTokens();
        return authService.getAccessToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        authService.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error('Session expired. Please login again.');
      }
    }

    return currentToken;
  }

  /**
   * Get CSRF token from cookies or meta tag
   */
  private getCsrfToken(): string | null {
    // Try to get from meta tag
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (metaTag) {
      return metaTag.content;
    }

    // Try to get from cookies
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(cookie => cookie.trim().startsWith('csrf_token='));
    if (csrfCookie) {
      return csrfCookie.split('=')[1];
    }

    return null;
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if error should not be retried
   */
  private shouldNotRetry(error: any): boolean {
    // Don't retry on authentication / client errors
    if (error?.status >= 400 && error?.status < 500) {
      return true;
    }

    // Don't retry on timeouts — the server may have already processed the request
    // (e.g. email already sent). Retrying risks duplicate side-effects.
    if (error?.name === 'TimeoutError' || error?.name === 'AbortError') {
      return true;
    }

    return false;
  }

  /**
   * Create standardized API error
   */
  private createApiError(
    error: Error, 
    status: number = 0, 
    code?: string, 
    details?: any
  ): ApiError {
    return {
      message: error.message || 'Unknown error occurred',
      status,
      code,
      details
    };
  }

  /**
   * Sleep utility for delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const httpClient = new HttpClient();

// Export convenience methods for common operations
export const api = {
  // User & Authentication
  getUserProfile: () => httpClient.get('/api/v1/users/profile'),
  updateUserProfile: (data: any) => httpClient.put('/api/v1/users/profile', data),

  // Projects
  getProjects: () => httpClient.get('/api/v1/projects'),
  getProject: (id: string) => httpClient.get(`/api/v1/projects/${id}`),
  createProject: (data: any) => httpClient.post('/api/v1/projects', data),
  updateProject: (id: string, data: any) => httpClient.put(`/api/v1/projects/${id}`, data),

  // BOQ
  getBOQs: () => httpClient.get('/api/v1/boq'),
  createBOQ: (data: any) => httpClient.post('/api/v1/boq', data),

  // Add more API methods as needed...
};